import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { Icon, type IconName } from './Icon';

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

// --- Button -----------------------------------------------------------------

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  icon?: IconName;
};

const VARIANTS: Record<string, string> = {
  // Hover is a ring bloom (1px → 3px of accent-dim) + brightness, never a lift.
  primary:
    'border border-accent bg-accent text-[color:var(--color-on-accent)] shadow-[0_0_0_1px_var(--color-accent-dim)] hover:brightness-110 hover:shadow-[0_0_0_3px_var(--color-accent-dim)] active:brightness-95 disabled:opacity-50 disabled:shadow-none',
  secondary: 'border border-border bg-surface text-primary hover:bg-surface-high disabled:opacity-50',
  ghost: 'border border-transparent bg-transparent text-secondary hover:text-primary hover:bg-surface',
  danger: 'border border-danger/60 bg-transparent text-danger hover:bg-danger/10',
};

// Touch targets are deliberately taller than Alchemist2's desktop sizes.
const SIZES: Record<string, string> = {
  sm: 'h-9 px-3 text-[12px]',
  md: 'h-11 px-4 text-[13px]',
  lg: 'h-14 px-6 text-[15px]',
};

export function Button({ variant = 'secondary', size = 'md', icon, className, children, ...rest }: ButtonProps) {
  return (
    <button
      className={cx(
        'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md font-medium transition duration-150',
        'focus:outline-none active:scale-[0.97] disabled:pointer-events-none',
        SIZES[size],
        VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      {icon && <Icon name={icon} size={size === 'lg' ? 16 : 14} />}
      {children}
    </button>
  );
}

/** Square icon-only button. `label` is required — it's the accessible name. */
export function IconButton({
  icon,
  label,
  variant = 'ghost',
  size = 'md',
  className,
  ...rest
}: Omit<ButtonProps, 'icon' | 'children'> & { icon: IconName; label: string }) {
  const px = size === 'sm' ? 'size-9' : size === 'lg' ? 'size-14' : 'size-11';
  return (
    <button
      aria-label={label}
      title={label}
      className={cx(
        'inline-flex shrink-0 items-center justify-center rounded-md transition duration-150',
        'focus:outline-none active:scale-[0.94] disabled:pointer-events-none disabled:opacity-50',
        px,
        VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      <Icon name={icon} size={size === 'lg' ? 20 : 16} />
    </button>
  );
}

// --- Inputs -----------------------------------------------------------------

/**
 * Fields sit on `surface-high` — lighter than the card they're in, inverted from typical
 * dark UIs. The inputMode/enterKeyHint defaults matter: they tell a tablet's OS keyboard
 * which layout to raise, which is what makes swipe-typing work.
 */
export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={cx(
        'touch w-full rounded-md border border-border bg-surface-high px-3',
        // 16px min on mobile: anything smaller makes iOS Safari zoom on focus.
        'text-[16px] text-primary placeholder:text-muted',
        'transition-colors focus:border-accent focus:outline-none',
        className,
      )}
      {...rest}
    />
  );
}

export function Textarea({
  className,
  ...rest
}: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={cx(
        'w-full rounded-md border border-border bg-surface-high px-3 py-2.5',
        'text-[16px] text-primary placeholder:text-muted',
        'transition-colors focus:border-accent focus:outline-none',
        className,
      )}
      {...rest}
    />
  );
}

export function Select({ className, children, ...rest }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className="relative">
      <select
        className={cx(
          'touch w-full appearance-none rounded-md border border-border bg-surface-high px-3 pr-9',
          'text-[15px] text-primary transition-colors focus:border-accent focus:outline-none',
          className,
        )}
        {...rest}
      >
        {children}
      </select>
      <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-muted">
        <Icon name="chevron-down" size={12} />
      </span>
    </div>
  );
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="block">
      <span className="eyebrow mb-1.5 block" style={{ letterSpacing: '0.18em' }}>
        {label}
      </span>
      {children}
      {hint && <span className="mt-1 block font-mono text-[12px] text-muted">{hint}</span>}
    </label>
  );
}

// --- Surfaces ---------------------------------------------------------------

/** Rests on a near-invisible hairline that *promotes* to a brighter one on hover. */
export function Card({
  interactive = false,
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement> & { interactive?: boolean }) {
  return (
    <div
      className={cx(
        'rounded-lg border border-border-subtle bg-surface transition-all duration-150',
        interactive && 'hover:border-border hover:bg-surface-high/60',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info';
  className?: string;
}) {
  const tones: Record<string, string> = {
    neutral: 'text-secondary border-border-subtle bg-surface-high',
    accent: 'text-accent border-accent/30 bg-[var(--color-accent-dim)]',
    success: 'text-success border-success/30 bg-success/10',
    warning: 'text-warning border-warning/30 bg-warning/10',
    danger: 'text-danger border-danger/30 bg-danger/10',
    info: 'text-info border-info/30 bg-info/10',
  };
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-sm border px-1.5 py-[1px] font-mono text-[11px] uppercase',
        tones[tone],
        className,
      )}
      style={{ letterSpacing: '0.12em' }}
    >
      {children}
    </span>
  );
}

/**
 * A member's avatar: their photo if they have one, otherwise a monogram in their colour.
 * Both read cleanly at any size and never depend on the host OS's emoji font.
 */
export function Avatar({
  name,
  color,
  photoUrl,
  size = 36,
  dimmed = false,
  ring = false,
}: {
  name: string;
  color: string;
  photoUrl?: string | null;
  size?: number;
  dimmed?: boolean;
  ring?: boolean;
}) {
  const initials = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();

  const shared = {
    width: size,
    height: size,
    border: `1px solid ${ring ? color : `color-mix(in srgb, ${color} 30%, transparent)`}`,
    boxShadow: ring ? `0 0 0 3px color-mix(in srgb, ${color} 14%, transparent)` : 'none',
    opacity: dimmed ? 0.4 : 1,
    transition: 'opacity 150ms ease, box-shadow 150ms ease, border-color 150ms ease',
  } as const;

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name}
        title={name}
        className="inline-block shrink-0 rounded-full object-cover select-none"
        style={shared}
        draggable={false}
      />
    );
  }

  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-full font-mono font-medium select-none"
      style={{
        ...shared,
        fontSize: Math.max(9, size * 0.34),
        letterSpacing: '0.04em',
        background: `color-mix(in srgb, ${color} 14%, transparent)`,
        color,
      }}
      title={name}
      aria-label={name}
    >
      {initials || '?'}
    </span>
  );
}

/** A ring where only the top border carries the accent. */
export function Spinner({ size = 16, label }: { size?: number; label?: string }) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-3">
      <span
        style={{ width: size, height: size, borderTopColor: 'var(--color-accent)' }}
        className="inline-block animate-spin rounded-full border-[1.5px] border-border"
      />
      {label && <span className="eyebrow">{label}</span>}
    </div>
  );
}

/** The only dashed border in the system. */
export function EmptyState({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border-subtle bg-surface/30 px-6 py-14 text-center">
      <span className="eyebrow">Nothing here</span>
      <p className="mt-2 font-display text-[18px] text-primary">{title}</p>
      {hint && <p className="mt-1 max-w-xs text-[13px] text-secondary">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function PageHeader({
  eyebrow,
  title,
  actions,
}: {
  eyebrow: string;
  title: string;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-6 flex items-end justify-between gap-4 border-b border-border-subtle pb-5">
      <div className="min-w-0">
        <span className="eyebrow">{eyebrow}</span>
        <h1 className="mt-1 truncate font-display text-[26px] leading-tight text-primary">{title}</h1>
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </header>
  );
}
